import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const storageKey = (checkoutId) => `apt300-checkout:${checkoutId}`;
const payingKey = (checkoutId) => `apt300-paying:${checkoutId}`;
const invalidEmailTlds = new Set(['con', 'cmo', 'comm', 'coom', 'om', 'cm', 'ne', 'ogr', 'et']);

function getEmailValidationMessage(value) {
  const email = String(value || '').trim().toLowerCase();

  if (!email) {
    return 'Email is required so we can send the tickets.';
  }

  if (email.length > 254 || /\s/.test(email)) {
    return 'Please enter a valid email address.';
  }

  const match = email.match(/^[^\s@]+@([^\s@]+\.[^\s@]+)$/);
  if (!match) {
    return 'Please enter a valid email address.';
  }

  const domain = match[1];
  if (domain.includes('..')) {
    return 'Please enter a valid email address.';
  }

  const tld = domain.split('.').pop();
  if (!tld || tld.length < 2 || invalidEmailTlds.has(tld)) {
    return 'Please check the end of your email address.';
  }

  return '';
}

function getPhoneValidationMessage(value) {
  const phone = String(value || '').trim();
  const digitCount = phone.replace(/\D/g, '').length;

  if (!phone) {
    return 'Phone number is required in case we need to contact you about your tickets.';
  }

  if (digitCount < 7 || digitCount > 20) {
    return 'Please enter a valid phone number.';
  }

  return '';
}

function requestCheckoutCancel(checkoutId) {
  if (!checkoutId || !process.env.REACT_APP_BACKEND_URL) {
    return;
  }

  const url = `${process.env.REACT_APP_BACKEND_URL}/storefront/checkout/cancel`;
  const body = JSON.stringify({ checkoutId });

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
    return;
  }

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => undefined);
}

function formatAmount(amount, currency) {
  if (!amount || !currency) {
    return '';
  }

  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
  }).format(amount / 100);
}

function formatCountdown(milliseconds) {
  const remainingSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function CheckoutForm({ checkoutData, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [email, setEmail] = useState(checkoutData.customerEmail || '');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const emailError = getEmailValidationMessage(email);
    if (emailError) {
      setErrorMessage(emailError);
      setIsSubmitting(false);
      return;
    }

    if (!fullName.trim()) {
      setErrorMessage('Name is required.');
      setIsSubmitting(false);
      return;
    }

    const phoneError = getPhoneValidationMessage(phone);
    if (phoneError) {
      setErrorMessage(phoneError);
      setIsSubmitting(false);
      return;
    }

    try {
      const contactResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/storefront/checkout/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          checkoutId: checkoutData.checkoutId,
          email: email.trim(),
          customerName: fullName.trim(),
          phone: phone.trim(),
        }),
      });

      const contactData = await contactResponse.json();
      if (!contactResponse.ok) {
        throw new Error(contactData.error || 'Failed to save checkout contact.');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to save checkout contact.');
      setIsSubmitting(false);
      return;
    }

    const returnUrl = `${window.location.origin}/checkout?checkout_id=${encodeURIComponent(
      checkoutData.checkoutId
    )}`;

    let result;
    try {
      sessionStorage.setItem(payingKey(checkoutData.checkoutId), '1');
      result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: 'if_required',
      });
    } catch (error) {
      setIsSubmitting(false);
      setErrorMessage(error.message || 'Payment failed. Please try again.');
      return;
    } finally {
      sessionStorage.removeItem(payingKey(checkoutData.checkoutId));
    }

    setIsSubmitting(false);

    const { error, paymentIntent } = result;

    if (error) {
      setErrorMessage(error.message || 'Payment failed. Please try again.');
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      onSuccess();
      return;
    }

    if (paymentIntent?.status === 'processing') {
      setErrorMessage('Your payment is processing. Refresh this page in a moment for an update.');
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <div style={styles.formGrid}>
        <input
          name="fullName"
          placeholder="Full name"
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
          required
          style={styles.input}
        />
        <input
          name="email"
          type="email"
          placeholder="Email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          style={styles.input}
        />
        <input
          name="phone"
          type="tel"
          placeholder="Phone number"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          autoComplete="tel"
          required
          style={styles.input}
        />
      </div>
      <PaymentElement />
      {errorMessage ? <div style={styles.error}>{errorMessage}</div> : null}
      <button type="submit" disabled={!stripe || !elements || isSubmitting} style={styles.submitButton}>
        {isSubmitting ? 'Processing...' : `Pay ${formatAmount(checkoutData.amount, checkoutData.currency)}`}
      </button>
    </form>
  );
}

function FreeCheckoutForm({ checkoutData, onSuccess }) {
  const [formState, setFormState] = useState({
    email: checkoutData.customerEmail || '',
    fullName: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (!formState.fullName.trim()) {
      setErrorMessage('Please fill out your name before continuing.');
      return;
    }

    const emailError = getEmailValidationMessage(formState.email);
    if (emailError) {
      setErrorMessage(emailError);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/storefront/checkout/free-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          checkoutId: checkoutData.checkoutId,
          email: formState.email.trim(),
          customerName: formState.fullName.trim(),
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Free checkout failed.');
      }

      onSuccess();
    } catch (error) {
      setErrorMessage(error.message || 'Free checkout failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <div style={styles.formGrid}>
        <input name="fullName" placeholder="Full name" value={formState.fullName} onChange={handleChange} required style={styles.input} />
        <input name="email" type="email" placeholder="Email" value={formState.email} onChange={handleChange} required style={styles.input} />
      </div>
      {errorMessage ? <div style={styles.error}>{errorMessage}</div> : null}
      <button type="submit" disabled={isSubmitting} style={styles.submitButton}>
        {isSubmitting ? 'Sending tickets...' : 'Send free tickets'}
      </button>
    </form>
  );
}

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [checkoutData, setCheckoutData] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [remainingMs, setRemainingMs] = useState(null);

  const params = new URLSearchParams(location.search);
  const checkoutId = params.get('checkout_id') || params.get('payment_intent');
  const redirectClientSecret = params.get('payment_intent_client_secret');

  useEffect(() => {
    if (!checkoutId) {
      setCheckoutData(null);
      return;
    }

    const stored = sessionStorage.getItem(storageKey(checkoutId));
    if (!stored) {
      setCheckoutData(null);
      return;
    }

    try {
      setCheckoutData(JSON.parse(stored));
    } catch (error) {
      console.error('Failed to parse stored checkout data:', error);
      setCheckoutData(null);
    }
  }, [checkoutId]);

  useEffect(() => {
    if (!checkoutData?.checkoutId) {
      return undefined;
    }

    const activeCheckoutId = checkoutData.checkoutId;
    const handlePageHide = () => {
      if (sessionStorage.getItem(payingKey(activeCheckoutId))) {
        return;
      }

      requestCheckoutCancel(activeCheckoutId);
      sessionStorage.removeItem(storageKey(activeCheckoutId));
    };

    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [checkoutData]);

  useEffect(() => {
    if (!checkoutData?.inventoryReservationExpiresAt) {
      setRemainingMs(null);
      return undefined;
    }

    const expiresAt = new Date(checkoutData.inventoryReservationExpiresAt).getTime();
    if (!Number.isFinite(expiresAt)) {
      setRemainingMs(null);
      return undefined;
    }

    const tick = () => {
      const nextRemaining = Math.max(0, expiresAt - Date.now());
      setRemainingMs(nextRemaining);
      if (nextRemaining === 0) {
        requestCheckoutCancel(checkoutData.checkoutId);
        sessionStorage.removeItem(storageKey(checkoutData.checkoutId));
        setStatusMessage('Your ticket hold expired. Please return to the event page to start again.');
      }
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [checkoutData]);

  useEffect(() => {
    if (!redirectClientSecret || !stripePromise) {
      return;
    }

    let isActive = true;

    setIsLoadingStatus(true);
    stripePromise
      .then((stripe) => {
        if (!stripe) {
          throw new Error('Stripe.js failed to load.');
        }

        return stripe.retrievePaymentIntent(redirectClientSecret);
      })
      .then(({ paymentIntent }) => {
        if (!isActive || !paymentIntent) {
          return;
        }

        if (paymentIntent.status === 'succeeded') {
          if (checkoutId) {
            sessionStorage.removeItem(storageKey(checkoutId));
          }
          navigate('/?success=1', { replace: true });
          return;
        }

        if (paymentIntent.status === 'processing') {
          setStatusMessage('Your payment is still processing. Refresh this page in a moment for an update.');
          return;
        }

        setStatusMessage('Your payment was not completed. Please try again.');
      })
      .catch((error) => {
        if (isActive) {
          console.error('Failed to retrieve payment intent:', error);
          setStatusMessage('We could not confirm the payment status. Please try again.');
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingStatus(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [checkoutId, navigate, redirectClientSecret]);

  const handleSuccess = () => {
    if (checkoutId) {
      sessionStorage.removeItem(storageKey(checkoutId));
    }
    navigate('/?success=1', { replace: true });
  };

  if (checkoutData?.requiresPayment && !process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    return (
      <div style={styles.page}>
        <div style={styles.panel}>
          <h1 style={styles.heading}>Stripe is not configured</h1>
          <p style={styles.copy}>Set REACT_APP_STRIPE_PUBLISHABLE_KEY before using the new checkout flow.</p>
        </div>
      </div>
    );
  }

  if (isLoadingStatus) {
    return (
      <div style={styles.page}>
        <div style={styles.panel}>
          <h1 style={styles.heading}>Checking payment status...</h1>
        </div>
      </div>
    );
  }

  if (statusMessage) {
    return (
      <div style={styles.page}>
        <div style={styles.panel}>
          <h1 style={styles.heading}>Checkout status</h1>
          <p style={styles.copy}>{statusMessage}</p>
          <Link to="/" style={styles.link}>Return to the home page</Link>
        </div>
      </div>
    );
  }

  if (!checkoutData) {
    return (
      <div style={styles.page}>
        <div style={styles.panel}>
          <h1 style={styles.heading}>Checkout expired</h1>
          <p style={styles.copy}>Start again from the event page to create a fresh checkout.</p>
          <Link to="/" style={styles.link}>Return to the home page</Link>
        </div>
      </div>
    );
  }

  if (checkoutData.requiresPayment && !checkoutData.clientSecret) {
    return (
      <div style={styles.page}>
        <div style={styles.panel}>
          <h1 style={styles.heading}>Checkout expired</h1>
          <p style={styles.copy}>Start again from the event page to create a fresh checkout.</p>
          <Link to="/" style={styles.link}>Return to the home page</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.layout}>
        <section style={styles.panel}>
          <p style={styles.eyebrow}>APT 300</p>
          <h1 style={styles.heading}>Complete your ticket purchase</h1>
          {remainingMs !== null ? (
            <div style={styles.holdNotice}>
              Your ticket is held for {formatCountdown(remainingMs)}
            </div>
          ) : null}
          <div style={styles.summaryBlock}>
            <div style={styles.summaryRow}>
              <span>Event</span>
              <strong>{checkoutData.product.title}</strong>
            </div>
            <div style={styles.summaryRow}>
              <span>When</span>
              <strong>{checkoutData.product.date} {checkoutData.product.time}</strong>
            </div>
            <div style={styles.summaryRow}>
              <span>Where</span>
              <strong>{checkoutData.product.venue || checkoutData.product.city}</strong>
            </div>
            {checkoutData.lineItems.map((item) => (
              <div key={item.id} style={styles.summaryRow}>
                <span>{item.title} x {item.quantity}</span>
                <strong>{item.price}</strong>
              </div>
            ))}
            <div style={styles.totalRow}>
              <span>Total</span>
              <strong>{formatAmount(checkoutData.amount, checkoutData.currency)}</strong>
            </div>
          </div>
        </section>

        <section style={styles.paymentPanel}>
          {checkoutData.requiresPayment ? (
            <Elements stripe={stripePromise} options={{ clientSecret: checkoutData.clientSecret }}>
              <CheckoutForm checkoutData={checkoutData} onSuccess={handleSuccess} />
            </Elements>
          ) : (
            <FreeCheckoutForm checkoutData={checkoutData} onSuccess={handleSuccess} />
          )}
        </section>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a0f0a 0%, #61210f 45%, #e59f71 100%)',
    padding: '32px 16px',
  },
  layout: {
    maxWidth: '1100px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '24px',
    alignItems: 'start',
  },
  panel: {
    background: 'rgba(255, 244, 235, 0.94)',
    borderRadius: '20px',
    padding: '32px',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.18)',
  },
  paymentPanel: {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '32px',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.18)',
  },
  eyebrow: {
    margin: '0 0 12px',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    fontSize: '12px',
    color: '#8f3518',
  },
  heading: {
    margin: '0 0 12px',
    fontFamily: 'Impact, sans-serif',
    fontSize: '42px',
    lineHeight: 1,
    color: '#2b120b',
  },
  copy: {
    margin: '0 0 24px',
    color: '#4f2b1e',
    lineHeight: 1.5,
  },
  summaryBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  holdNotice: {
    margin: '0 0 18px',
    padding: '10px 12px',
    borderRadius: '8px',
    background: 'rgba(143, 53, 24, 0.1)',
    color: '#8f3518',
    fontWeight: 700,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'baseline',
    color: '#2b120b',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'baseline',
    color: '#2b120b',
    paddingTop: '16px',
    marginTop: '8px',
    borderTop: '1px solid rgba(43, 18, 11, 0.15)',
    fontSize: '20px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '12px',
  },
  input: {
    width: '100%',
    border: '1px solid rgba(43, 18, 11, 0.15)',
    borderRadius: '12px',
    padding: '14px 16px',
    fontSize: '15px',
    boxSizing: 'border-box',
  },
  submitButton: {
    border: 'none',
    borderRadius: '999px',
    padding: '14px 20px',
    fontSize: '16px',
    fontWeight: 700,
    background: '#8f3518',
    color: '#fff7f2',
    cursor: 'pointer',
  },
  error: {
    color: '#b3261e',
    fontSize: '14px',
  },
  link: {
    color: '#8f3518',
    fontWeight: 700,
  },
};
